import { Injectable } from '@odinjs/odin';
import { AutocompleteAPI, RadioGroupAPI, SelectableItem } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { LanguageService } from '@philips-internal/oswald-ui-core/lib/services';

import { Utils } from '../utils';

const STATUS_ITEMS: SelectableItem[] = [
  {
    key: 'all',
    label: '@text.status/t',
    value: '',
  },
  {
    key: 'active',
    label: '@text.status/a',
    value: 'true',
  },
  {
    key: 'inactive',
    label: '@text.status/i',
    value: 'false',
  },
];

const APPLICATION_TYPE_ITEMS: SelectableItem[] = [
  {
    key: 'all',
    label: '@text.status/t',
    value: '',
  },
  {
    key: 'EMR',
    label: 'EMR',
    value: 'E',
  },
  {
    key: 'HSDP',
    label: 'HSDP',
    value: 'H',
  },
];

const MAINTENANCE_STATUS_ITEMS: SelectableItem[] = [
  {
    key: 'all',
    label: '@text.status/t',
    value: '',
  },
  {
    key: 'critical',
    label: 'maintenance.critical',
    value: 'true',
  },
  {
    key: 'scheduled',
    label: 'maintenance.scheduled',
    value: 'false',
  },
];

const CLASSIFICATION_FILTER: Record<string, Value>[] = [
  {
    label: '@kpiopened.classification/d',
    value: 'D',
  },
  {
    label: '@kpiopened.classification/e',
    value: 'E',
  },
  {
    label: '@kpiopened.classification/s',
    value: 'S',
  },
  {
    label: '@kpiopened.classification/t',
    value: 'T',
  },
];

const MONTH_FILTER: Record<string, Value>[] = [
  {
    label: 'Janeiro',
    value: 'Janeiro',
  },
  {
    label: 'Fevereiro',
    value: 'Fevereiro',
  },
  {
    label: 'Março',
    value: 'Março',
  },
  {
    label: 'Abril',
    value: 'Abril',
  },
  {
    label: 'Maio',
    value: 'Maio',
  },
  {
    label: 'Junho',
    value: 'Junho',
  },
  {
    label: 'Julho',
    value: 'Julho',
  },
  {
    label: 'Agosto',
    value: 'Agosto',
  },
  {
    label: 'Setembro',
    value: 'Setembro',
  },
  {
    label: 'Outubro',
    value: 'Outubro',
  },
  {
    label: 'Novembro',
    value: 'Novembro',
  },
  {
    label: 'Dezembro',
    value: 'Dezembro',
  },

];

const YEAR_FILTER: Record<string, Value>[] = [
  {
    label: '2021',
    value: '2021',
  },
  {
    label: '2022',
    value: '2022',
  },
];

const TIME_COURSE_FILTER: Record<string, Value>[] = [
  {
    label: '@text.periodo/t',
    value: 'T',
  },
  {
    label: '@text.periodo/s',
    value: 'S',
  },
  {
    label: '@text.periodo/a',
    value: 'A',
  },
];

const CONTRACT_FILTER: Record<string, Value>[] = [
  {
    label: 'SLA with penalty',
    value: '1',
  },
  {
    label: 'SLA with contract',
    value: '2',
  },
  {
    label: 'All defects',
    value: '3',
  },
  {
    label: 'Other',
    value: '998',
  },
];

export {
  STATUS_ITEMS,
  APPLICATION_TYPE_ITEMS,
  MAINTENANCE_STATUS_ITEMS,
  CLASSIFICATION_FILTER,
  CONTRACT_FILTER,
  TIME_COURSE_FILTER,
  YEAR_FILTER,
  MONTH_FILTER,
};

@Injectable({})
export class FilterService {

  static mountRadioGroupStatus(radioGroupAPI: RadioGroupAPI, defaultSelectableItem: string = 'all'): void {
    radioGroupAPI.setData(STATUS_ITEMS);
    const selectableItem = STATUS_ITEMS.find(item => item.key == defaultSelectableItem);
    if (selectableItem) {
      radioGroupAPI.setValue(selectableItem);
    }
  }

  static mountRadioGroupApplicationType(radioGroupAPI: RadioGroupAPI, defaultSelectableItem: string = 'all'): void {
    radioGroupAPI.setData(APPLICATION_TYPE_ITEMS);
    const selectableItem = APPLICATION_TYPE_ITEMS.find(item => item.key == defaultSelectableItem);
    if (selectableItem) {
      radioGroupAPI.setValue(selectableItem);
    }
  }

  static mountRadioGroupMaintenanceStatus(radioGroupAPI: RadioGroupAPI, defaultSelectableItem: string = 'all'): void {
    radioGroupAPI.setData(MAINTENANCE_STATUS_ITEMS);
    const selectableItem = MAINTENANCE_STATUS_ITEMS.find(item => item.key == defaultSelectableItem);
    if (selectableItem) {
      radioGroupAPI.setValue(selectableItem);
    }
  }

  static onAfterFetchAutocompleteYear(autocompleteYear: AutocompleteAPI): void {
    const data = autocompleteYear.getData();
    if (data && data.length > 0) {
      const currentYear = Utils.getCurrentYear();
      let current = data.find(d => d.value == currentYear);
      if (!current) {
        current = data.slice().pop();
      }
      autocompleteYear.setValue(current);
    }
  }

  static onAfterFetchAutocompleteMonth(autocompleteMonth: AutocompleteAPI): void {
    const data = autocompleteMonth.getData();
    if (data && data.length > 0) {
      const currentMonth = Utils.getCurrentMoth();
      let current = data.find(d => d.value == currentMonth);
      if (!current) {
        current = data.slice().pop();
      }
      autocompleteMonth.setValue(current);
    }
  }

  static mountFieldMonth(): Record<string, Value>[] {
    const locale = LanguageService.getCurrentLanguage();
    const months = Array.from(
      Array(12).keys(),
      key => Intl.DateTimeFormat(locale, {
        month: 'long',
      }).format(new Date(0, key))
    );
    const monthRecord: Record<string, Value>[] = [];
    let monthNumber: number = 0;
    months.forEach(month => monthRecord.push({
      label: Utils.capitalizeFirst(month),
      value: ++monthNumber,
    }));
    return monthRecord;
  }

  static mountAutoCompleteClassification(autoComplete: AutocompleteAPI): void {
    autoComplete.setEntry('label');
    autoComplete.setPrimaryKey('value');
    autoComplete.setData(CLASSIFICATION_FILTER);
  }

  static mountAutoCompleteYear(autoComplete: AutocompleteAPI): void {
    autoComplete.setEntry('label');
    autoComplete.setPrimaryKey('value');
    autoComplete.setData(YEAR_FILTER);
  }

  static mountAutoCompleteMonth(autoComplete: AutocompleteAPI): void {
    autoComplete.setEntry('label');
    autoComplete.setPrimaryKey('value');
    autoComplete.setData(MONTH_FILTER);
  }

  static mountAutoCompleteTimeCourse(autoComplete: AutocompleteAPI): void {
    autoComplete.setEntry('label');
    autoComplete.setPrimaryKey('value');
    autoComplete.setData(TIME_COURSE_FILTER);
  }

  /**
   * @deprecated not use this method
   * */
  static mountAutoCompleteContract(autoComplete: AutocompleteAPI): void {
    autoComplete.setEntry('label');
    autoComplete.setPrimaryKey('value');
    autoComplete.setData(CONTRACT_FILTER);
  }
}
